import { definePreset } from "@primevue/themes";
import Aura from "@primevue/themes/aura";

export const qmTheme = definePreset(Aura, {
    primitive: {
        // Raw design values - foundational, component-agnostic tokens
        border: {
            radius: {
                none: "0",
                xs: "2px",
                sm: "4px",
                md: "6px",
                lg: "8px",
                xl: "12px",
            },
            width: {
                none: "0",
                thin: "1px",
                medium: "2px",
                thick: "4px",
            },
            color: {
                primary: "#E3E3E3",
            },
        },
        borderRadius: {
            none: "0",
            xs: "2px",
            sm: "4px",
            md: "6px",
            lg: "8px",
            xl: "12px",
        },
        spacing: {
            none: "0",
            xxs: "2px",
            xs: "4px",
            sm: "8px",
            md: "12px",
            lg: "16px",
            xl: "24px",
            xxl: "32px",
        },
        borderWidth: {
            none: "0",
            thin: "1px",
            medium: "2px",
            thick: "4px",
        },
        fontSize: {
            xs: "0.75em",
            sm: "0.8571428571em",
            base: "1em",
            lg: "1.1428571429em",
            xl: "1.2857142857em",
        },
        shadows: {
            none: "none",
            sm: "0 1px 2px 0 rgba(18, 18, 23, 0.05)",
            md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
            lg: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
        },
        transition: {
            duration: "0.2s",
        },
        // Color palettes - raw color values
        emerald: {
            50: "#ecfdf5",
            100: "#d1fae5",
            200: "#a7f3d0",
            300: "#6ee7b7",
            400: "#34d399",
            500: "#10b981",
            600: "#059669",
            700: "#047857",
            800: "#065f46",
            900: "#064e3b",
            950: "#022c22",
        },
        green: {
            50: "#f2f9f2",
            100: "#cae5ca",
            200: "#a2d0a2",
            300: "#51a451",
            400: "#288d28",
            500: "#007600",
            600: "#006500",
            700: "#005300",
            800: "#004100",
            900: "#002e00",
            950: "#001b00",
        },
        lime: {
            50: "#f7fee7",
            100: "#ecfccb",
            200: "#d9f99d",
            300: "#bef264",
            400: "#a3e635",
            500: "#84cc16",
            600: "#65a30d",
            700: "#4d7c0f",
            800: "#3f6212",
            900: "#365314",
            950: "#1a2e05",
        },
        red: {
            50: "#fcebe8",
            100: "#f8cec7",
            200: "#f2b1a7",
            300: "#ec9588",
            400: "#e57969",
            500: "#dc5b48",
            600: "#d4442e",
            700: "#b33724",
            800: "#922b1b",
            900: "#701f13",
            950: "#4c140c",
        },
        orange: {
            50: "#fff7ed",
            100: "#ffedd5",
            200: "#fed7aa",
            300: "#fdba74",
            400: "#fb923c",
            500: "#f97316",
            600: "#ea580c",
            700: "#c2410c",
            800: "#9a3412",
            900: "#7c2d12",
            950: "#431407",
        },
        amber: {
            50: "#fffbeb",
            100: "#fef3c7",
            200: "#fde68a",
            300: "#fcd34d",
            400: "#fbbf24",
            500: "#f59e0b",
            600: "#d97706",
            700: "#b45309",
            800: "#92400e",
            900: "#78350f",
            950: "#451a03",
        },
        yellow: {
            50: "#fefce8",
            100: "#fef9c3",
            200: "#fef08a",
            300: "#fde047",
            400: "#facc15",
            500: "#eab308",
            600: "#ca8a04",
            700: "#a16207",
            800: "#854d0e",
            900: "#713f12",
            950: "#422006",
        },
        teal: {
            50: "#f0fdfa",
            100: "#ccfbf1",
            200: "#99f6e4",
            300: "#5eead4",
            400: "#2dd4bf",
            500: "#14b8a6",
            600: "#0d9488",
            700: "#0f766e",
            800: "#115e59",
            900: "#134e4a",
            950: "#042f2e",
        },
        cyan: {
            50: "#ecfeff",
            100: "#cffafe",
            200: "#a5f3fc",
            300: "#67e8f9",
            400: "#22d3ee",
            500: "#06b6d4",
            600: "#0891b2",
            700: "#0e7490",
            800: "#155e75",
            900: "#164e63",
            950: "#083344",
        },
        sky: {
            50: "#f0f9ff",
            100: "#e0f2fe",
            200: "#bae6fd",
            300: "#7dd3fc",
            400: "#38bdf8",
            500: "#0ea5e9",
            600: "#0284c7",
            700: "#0369a1",
            800: "#075985",
            900: "#0c4a6e",
            950: "#082f49",
        },
        blue: {
            50: "#eff6ff",
            100: "#dbeafe",
            200: "#bfdbfe",
            300: "#93c5fd",
            400: "#60a5fa",
            500: "#3b82f6",
            600: "#2563eb",
            700: "#1d4ed8",
            800: "#1e40af",
            900: "#1e3a8a",
            950: "#172554",
        },
        indigo: {
            50: "#eef2ff",
            100: "#e0e7ff",
            200: "#c7d2fe",
            300: "#a5b4fc",
            400: "#818cf8",
            500: "#6366f1",
            600: "#4f46e5",
            700: "#4338ca",
            800: "#3730a3",
            900: "#312e81",
            950: "#1e1b4b",
        },
        violet: {
            50: "#f5f3ff",
            100: "#ede9fe",
            200: "#ddd6fe",
            300: "#c4b5fd",
            400: "#a78bfa",
            500: "#8b5cf6",
            600: "#7c3aed",
            700: "#6d28d9",
            800: "#5b21b6",
            900: "#4c1d95",
            950: "#2e1065",
        },
        purple: {
            50: "#faf5ff",
            100: "#f3e8ff",
            200: "#e9d5ff",
            300: "#d8b4fe",
            400: "#c084fc",
            500: "#a855f7",
            600: "#9333ea",
            700: "#7e22ce",
            800: "#6b21a8",
            900: "#581c87",
            950: "#3b0764",
        },
        fuchsia: {
            50: "#fdf4ff",
            100: "#fae8ff",
            200: "#f5d0fe",
            300: "#f0abfc",
            400: "#e879f9",
            500: "#d946ef",
            600: "#c026d3",
            700: "#a21caf",
            800: "#86198f",
            900: "#701a75",
            950: "#4a044e",
        },
        pink: {
            50: "#fdf2f8",
            100: "#fce7f3",
            200: "#fbcfe8",
            300: "#f9a8d4",
            400: "#f472b6",
            500: "#ec4899",
            600: "#db2777",
            700: "#be185d",
            800: "#9d174d",
            900: "#831843",
            950: "#500724",
        },
        rose: {
            50: "#fff1f2",
            100: "#ffe4e6",
            200: "#fecdd3",
            300: "#fda4af",
            400: "#fb7185",
            500: "#f43f5e",
            600: "#e11d48",
            700: "#be123c",
            800: "#9f1239",
            900: "#881337",
            950: "#4c0519",
        },
        slate: {
            50: "#f8fafc",
            100: "#f1f5f9",
            200: "#e2e8f0",
            300: "#cbd5e1",
            400: "#94a3b8",
            500: "#64748b",
            600: "#475569",
            700: "#334155",
            800: "#1e293b",
            900: "#0f172a",
            950: "#020617",
        },
        gray: {
            50: "#f9fafb",
            100: "#f3f4f6",
            200: "#e5e7eb",
            300: "#d1d5db",
            400: "#9ca3af",
            500: "#6b7280",
            600: "#4b5563",
            700: "#374151",
            800: "#1f2937",
            900: "#111827",
            950: "#030712",
        },
        zinc: {
            50: "#fafafa",
            100: "#f4f4f5",
            200: "#e4e4e7",
            300: "#d4d4d8",
            400: "#a1a1aa",
            500: "#71717a",
            600: "#52525b",
            700: "#3f3f46",
            800: "#27272a",
            900: "#18181b",
            950: "#09090b",
        },
        neutral: {
            50: "#f6f6f6",
            100: "#eeeeee",
            200: "#e3e3e3",
            300: "#a7a7a7",
            400: "#707070",
            500: "#575757",
            600: "#444444",
            700: "#2f2f2f",
            800: "#222222",
            900: "#1e1e1e",
            950: "#111111",
        },
        stone: {
            50: "#fafaf9",
            100: "#f5f5f4",
            200: "#e7e5e4",
            300: "#d6d3d1",
            400: "#a8a29e",
            500: "#78716c",
            600: "#57534e",
            700: "#44403c",
            800: "#292524",
            900: "#1c1917",
            950: "#0c0a09",
        },
        // QuoteMedia brand blue ramp (Figma `qm-blue.*`). Distinct from the
        // generic Tailwind `blue` palette above — brand uses `qm-blue-700`
        // (#0477d1) as the primary action color.
        qmBlue: {
            50: "#f2f8fe",
            100: "#c0def7",
            200: "#a5d0f3",
            300: "#89c1ef",
            400: "#53a4e4",
            500: "#3795de",
            600: "#1d86d8",
            700: "#0477d1",
            800: "#00407f",
            900: "#002c5a",
            950: "#001831",
        },
        // Dedicated dark-mode neutral ramp (Figma `dark.*`). Warmer than
        // zinc/gray — the dark `surface.*` chain references these directly
        // rather than inverting the light `neutral.*` ramp. Named
        // `darkSurface` rather than `dark` because PrimeVue reserves the
        // `dark` key for the colorScheme namespace and silently drops a
        // primitive with the same name.
        darkSurface: {
            50: "#ffffff",
            100: "#eeeeee",
            200: "#e3e3e3",
            300: "#a7a7a7",
            400: "#707070",
            500: "#444444",
            600: "#2f2f2f",
            700: "#222222",
            800: "#1e1e1e",
            900: "#191919",
            950: "#111111",
        },
    },
    semantic: {
        // Component-oriented, meaningful abstractions
        transitionDuration: "0.2s",
        focusRing: {
            width: "{border.width.thin}",
            style: "solid",
            color: "{primary.color}",
            offset: "2px",
            shadow: "none",
        },
        disabledOpacity: "0.6",
        iconSize: "{fontSize.base}",
        anchorGutter: "2px",
        // Semantic `primary.*` aliases the QuoteMedia brand `qmBlue.*` ramp
        // 1:1 so `primary.700` is the brand action color (#0477d1) — same
        // level the Figma tokens use. Updating one of these will keep them
        // in sync via the alias.
        primary: {
            50: "{qmBlue.50}",
            100: "{qmBlue.100}",
            200: "{qmBlue.200}",
            300: "{qmBlue.300}",
            400: "{qmBlue.400}",
            500: "{qmBlue.500}",
            600: "{qmBlue.600}",
            700: "{qmBlue.700}",
            800: "{qmBlue.800}",
            900: "{qmBlue.900}",
            950: "{qmBlue.950}",
        },

        formField: {
            // Figma `--form-field-padding-x/y` (0.5rem / 0.375rem). Tighter
            // than the previous spacing.md / spacing.sm pair (12px / 8px),
            // matching the 32px control height set by the component css hooks.
            paddingX: "0.5rem",
            paddingY: "0.375rem",
            fontSize: "{fontSize.sm}",
            sm: {
                fontSize: "{fontSize.xs}",
                paddingX: "{spacing.xs}",
                paddingY: "{spacing.xs}",
            },
            lg: {
                fontSize: "{fontSize.base}",
                paddingX: "{spacing.md}",
                paddingY: "{spacing.sm}",
            },
            borderRadius: "{border.radius.sm}",
            focusRing: {
                width: "0",
                style: "none",
                color: "transparent",
                offset: "0",
                shadow: "none",
            },
            transitionDuration: "{transition.duration}",
        },
        list: {
            padding: "{spacing.xs} {spacing.xs}",
            borderRadius: "{border.radius.sm}",
            gap: "2px",
            header: {
                padding: "{spacing.sm} {spacing.lg} {spacing.xs} {spacing.lg}",
            },
            option: {
                padding: "{spacing.sm}",
                borderRadius: "{border.radius.sm}",
            },
            optionGroup: {
                padding: "{spacing.sm} {spacing.md}",
                fontWeight: "600",
            },
        },
        content: {
            // Figma `--content-border-radius: var(--border-radius-sm)`
            // (4px). Previously was xs (2px) — bumped so cards, panels,
            // popovers, and other content surfaces feel less sharp.
            borderRadius: "{border.radius.sm}",
        },
        mask: {
            transitionDuration: "0.15s",
        },
        navigation: {
            list: {
                padding: "{spacing.xs} {spacing.xs}",
                gap: "2px",
            },
            item: {
                padding: "{spacing.sm} {spacing.md}",
                borderRadius: "{border.radius.sm}",
                gap: "{spacing.sm}",
            },
            submenuLabel: {
                padding: "{spacing.sm} {spacing.md}",
                fontWeight: "600",
            },
            submenuIcon: {
                size: "{fontSize.sm}",
            },
        },
        overlay: {
            select: {
                borderRadius: "{border.radius.sm}",
            },
            popover: {
                borderRadius: "{border.radius.sm}",
                padding: "8px",
                shadow: "{shadows.md}",
            },
            modal: {
                borderRadius: "{border.radius.xl}",
                // Aura defaults this to spacing.xl (24px), which is far
                // too loose for our Drawer chrome. The Aura Drawer preset
                // reads `header.padding`, `content.padding`, and
                // `footer.padding` from this single token, so pinning it
                // here lets us remove the per-component drawer override.
                padding: "{spacing.md}",
                shadow: "{shadows.lg}",
            },
            navigation: {
                shadow: "{shadows.md}",
            },
        },
        dialog: {
            root: {
                borderRadius: "16px !important",
            },
            header: {
                padding: "16px !important",
            },
            content: {
                padding: "16px !important",
            },
            footer: {
                padding: "16px !important",
            },
            closebutton: {
                margin: "0 !important",
            },
            color: "{text.primary}",
        },

        checkbox: {
            width: "16px !important",
            height: "16px !important",
            border: "{border.width.thin}",
            borderRadius: "{border.radius.sm}",
            iconSize: "10px !important",
        },
        chip: {
            padding: "{spacing.xs} {spacing.sm}",
            borderRadius: "{border.radius.sm}",
            gap: "{spacing.xs}",
        },
        multiselect: {
            list: {
                header: {
                    padding: "0 !important",
                },
            },
        },
        colorScheme: {
            light: {
                // Figma chains surface.* through the neutral.* palette
                // (warm grays). Keeping surface.0 at pure white but
                // delegating the rest preserves a single source of truth.
                surface: {
                    0: "#ffffff",
                    50: "{neutral.50}",
                    100: "{neutral.100}",
                    200: "{neutral.200}",
                    300: "{neutral.300}",
                    400: "{neutral.400}",
                    500: "{neutral.500}",
                    600: "{neutral.600}",
                    700: "{neutral.700}",
                    800: "{neutral.800}",
                    900: "{neutral.900}",
                    950: "{neutral.950}",
                },
                // Per Figma: primary action color sits at the 700 step of
                // the brand ramp (#0477d1), hover lifts to 600 (#1d86d8).
                primary: {
                    color: "{primary.700}",
                    contrastColor: "#ffffff",
                    hoverColor: "{primary.600}",
                    activeColor: "{primary.700}",
                },
                // Figma highlight chains: subtle tinted background at the
                // 50 step, deep navy text at 900/950 for strong contrast.
                highlight: {
                    background: "{primary.50}",
                    focusBackground: "{primary.50}",
                    color: "{primary.900}",
                    focusColor: "{primary.950}",
                },
                mask: {
                    background: "rgba(0,0,0,0.4)",
                    color: "{surface.200}",
                },
                formField: {
                    background: "{surface.0}",
                    // Figma `--form-field-disabled-background: var(--surface-0)`
                    // — disabled fields keep the same background as enabled;
                    // affordance comes from disabled text color + cursor.
                    disabledBackground: "{surface.0}",
                    filledBackground: "{surface.50}",
                    filledHoverBackground: "{surface.50}",
                    filledFocusBackground: "{surface.50}",
                    // Figma `--form-field-border-color: var(--surface-200)`
                    // (#e3e3e3) — same as our `border.color.primary` so
                    // form-field borders blend with the rest of the UI's
                    // chrome. Hover lifts one step (#a7a7a7) for affordance.
                    borderColor: "{surface.200}",
                    hoverBorderColor: "{surface.300}",
                    focusBorderColor: "{primary.color}",
                    invalidBorderColor: "{red.400}",
                    color: "{text.primary}",
                    disabledColor: "{surface.500}",
                    placeholderColor: "{surface.500}",
                    invalidPlaceholderColor: "{red.600}",
                    floatLabelColor: "{surface.500}",
                    floatLabelFocusColor: "{primary.600}",
                    floatLabelActiveColor: "{surface.500}",
                    floatLabelInvalidColor: "{form.field.invalid.placeholder.color}",
                    iconColor: "{surface.500}",
                    shadow: "{shadows.none}",
                },
                // Figma light text chain (`[data-theme="light"]`).
                // text.primary lands on surface.800 (#222222) — the darkest
                // gray below pure black. positive/negative/warning/link
                // are Figma's calibrated semantic colors — hardcoded
                // hex because the contrast targets are independent of
                // the green/red palette ramps.
                text: {
                    primary: "{surface.800}",
                    secondary: "{surface.500}",
                    color: "{surface.800}",
                    hoverColor: "{surface.800}",
                    hoverMutedColor: "{surface.600}",
                    mutedColor: "{surface.400}",
                    positive: "#007600",
                    negative: "#c71c28",
                    warning: "#ffffff",
                    link: "{primary.900}",
                },
                content: {
                    background: "{surface.0}",
                    hoverBackground: "{surface.100}",
                    borderColor: "{surface.200}",
                    color: "{text.primary}",
                    hoverColor: "{text.hover.color}",
                },
                overlay: {
                    select: {
                        background: "{surface.0}",
                        borderColor: "{surface.200}",
                        color: "{text.primary}",
                    },
                    popover: {
                        background: "{surface.0}",
                        borderColor: "{surface.200}",
                        color: "{text.primary}",
                    },
                    modal: {
                        background: "{surface.0}",
                        borderColor: "{surface.200}",
                        color: "{text.primary}",
                    },
                    menu: {
                        borderColor: "{surface.200}",
                        color: "{text.primary}",
                        borderRadius: "{border.radius.xl}",
                    },
                },
                list: {
                    option: {
                        focusBackground: "{surface.100}",
                        selectedBackground: "{highlight.background}",
                        selectedFocusBackground: "{highlight.focus.background}",
                        color: "{text.primary}",
                        focusColor: "{text.hover.color}",
                        selectedColor: "{highlight.color}",
                        selectedFocusColor: "{highlight.focus.color}",
                        icon: {
                            color: "{surface.500}",
                            focusColor: "{surface.500}",
                        },
                    },
                    optionGroup: {
                        background: "transparent",
                        color: "{text.muted.color}",
                    },
                },
                navigation: {
                    item: {
                        focusBackground: "{surface.100}",
                        activeBackground: "{surface.100}",
                        color: "{text.primary}",
                        focusColor: "{text.hover.color}",
                        activeColor: "{text.hover.color}",
                        icon: {
                            color: "{surface.500}",
                            focusColor: "{surface.500}",
                            activeColor: "{surface.500}",
                        },
                    },
                    submenuLabel: {
                        background: "transparent",
                        color: "{text.muted.color}",
                    },
                    submenuIcon: {
                        color: "{surface.500}",
                        focusColor: "{surface.500}",
                        activeColor: "{surface.500}",
                    },
                },
                chip: {
                    background: "{surface.100}",
                    color: "{text.primary}",
                    icon: {
                        color: "{surface.500}",
                        focusColor: "{surface.500}",
                    },
                },
                tabs: {
                    tab: {
                        color: "{text.primary}",
                        background: "transparent",
                        hoverBackground: "{surface.100}",
                        borderColor: "{surface.200}",
                        hoverBorderColor: "{surface.200}",
                        // Match Figma's underline-style active state: kept
                        // transparent so the bottom-border (set at component
                        // level) carries the affordance.
                        activeBackground: "transparent",
                        activeBorderColor: "{primary.color}",
                    },
                },
                dialog: {
                    color: "{text.primary}",
                },
            },
            dark: {
                // Figma chains the dark surface through the dedicated
                // `darkSurface.*` palette (warm dark grays), not by
                // inverting the light neutral ramp. surface.0 is true
                // black per Figma's `[data-theme="Dark"]`.
                surface: {
                    0: "#000000",
                    50: "{darkSurface.50}",
                    100: "{darkSurface.100}",
                    200: "{darkSurface.200}",
                    300: "{darkSurface.300}",
                    400: "{darkSurface.400}",
                    500: "{darkSurface.500}",
                    600: "{darkSurface.600}",
                    700: "{darkSurface.700}",
                    800: "{darkSurface.800}",
                    900: "{darkSurface.900}",
                    950: "{darkSurface.950}",
                },
                // Dark theme keeps the same brand-blue levels as light per
                // Figma (no inverted ramp here) — same color reads on dark
                // surface 950, with white contrast text on top.
                primary: {
                    color: "{primary.700}",
                    contrastColor: "#ffffff",
                    hoverColor: "{primary.600}",
                    activeColor: "{primary.700}",
                },
                // Dark highlight per Figma: deep navy 950 background under
                // brand-blue 400 text. `tabHoverBackground` is a qmodii-
                // specific extension consumed by the tabs override below.
                highlight: {
                    background: "{primary.950}",
                    focusBackground: "{primary.950}",
                    color: "{primary.400}",
                    focusColor: "{primary.400}",
                    tabHoverBackground: "#112634",
                },
                mask: {
                    background: "rgba(0,0,0,0.6)",
                    color: "{surface.200}",
                },
                formField: {
                    background: "{surface.950}",
                    // Figma dark `--form-field-disabled-background:
                    // var(--surface-900)` — one step lighter than the
                    // enabled surface so disabled fields still read.
                    disabledBackground: "{surface.900}",
                    filledBackground: "{surface.800}",
                    filledHoverBackground: "{surface.800}",
                    filledFocusBackground: "{surface.800}",
                    // Figma dark `--form-field-border-color: var(--surface-700)`
                    // — one step darker than the surrounding container so the
                    // border still reads on the dark background.
                    borderColor: "{surface.700}",
                    hoverBorderColor: "{surface.600}",
                    focusBorderColor: "{primary.color}",
                    invalidBorderColor: "{red.300}",
                    color: "{surface.0}",
                    // Figma dark text-on-form chains land at surface.300
                    // (#a7a7a7) — same hue for disabled + placeholder.
                    disabledColor: "{surface.300}",
                    placeholderColor: "{surface.300}",
                    invalidPlaceholderColor: "{red.400}",
                    floatLabelColor: "{surface.400}",
                    floatLabelFocusColor: "{primary.color}",
                    floatLabelActiveColor: "{surface.400}",
                    floatLabelInvalidColor: "{form.field.invalid.placeholder.color}",
                    iconColor: "{surface.500}",
                    shadow: "none",
                },
                // Figma dark text chain: primary at surface.200 (#e3e3e3,
                // off-white). Hover lifts toward surface.100 — never to
                // pure white, to keep contrast against the dark surface.
                // positive/negative are brighter than their light-mode
                // counterparts for adequate contrast on dark backgrounds.
                text: {
                    primary: "{surface.200}",
                    secondary: "{surface.300}",
                    color: "{surface.200}",
                    hoverColor: "{surface.100}",
                    mutedColor: "{surface.400}",
                    hoverMutedColor: "{surface.200}",
                    positive: "#3dab35",
                    negative: "#ea4f3e",
                    warning: "#ffffff",
                    link: "#ffffff",
                },
                content: {
                    background: "{surface.900}",
                    hoverBackground: "{surface.800}",
                    borderColor: "{surface.700}",
                    color: "{text.primary}",
                    hoverColor: "{text.hover.color}",
                },
                overlay: {
                    select: {
                        background: "{surface.900}",
                        borderColor: "{surface.700}",
                        color: "{text.primary}",
                    },
                    popover: {
                        background: "{surface.900}",
                        borderColor: "{surface.700}",
                        color: "{text.primary}",
                    },
                    modal: {
                        background: "{surface.900}",
                        borderColor: "{surface.700}",
                        color: "{text.primary}",
                    },
                    menu: {
                        background: "{surface.900}",
                        borderColor: "{surface.700}",
                        color: "{text.primary}",
                    },
                },
                list: {
                    option: {
                        focusBackground: "{surface.800}",
                        selectedBackground: "{highlight.background}",
                        selectedFocusBackground: "{highlight.focus.background}",
                        color: "{text.primary}",
                        focusColor: "{text.hover.color}",
                        selectedColor: "{highlight.color}",
                        selectedFocusColor: "{highlight.focus.color}",
                        icon: {
                            color: "{surface.500}",
                            focusColor: "{surface.400}",
                        },
                    },
                    optionGroup: {
                        background: "transparent",
                        color: "{text.muted.color}",
                    },
                },
                navigation: {
                    item: {
                        focusBackground: "{surface.800}",
                        activeBackground: "{surface.800}",
                        color: "{text.primary}",
                        focusColor: "{text.hover.color}",
                        activeColor: "{text.hover.color}",
                        icon: {
                            color: "{surface.500}",
                            focusColor: "{surface.400}",
                            activeColor: "{surface.400}",
                        },
                    },
                    submenuLabel: {
                        background: "transparent",
                        color: "{text.muted.color}",
                    },
                    submenuIcon: {
                        color: "{surface.500}",
                        focusColor: "{surface.400}",
                        activeColor: "{surface.400}",
                    },
                },
                tabs: {
                    tab: {
                        color: "{text.primary}",
                        background: "transparent",
                        hoverBackground: "{surface.700}",
                        borderColor: "{surface.600}",
                        hoverBorderColor: "{surface.600}",
                        activeBackground: "{surface.highlight.tabHoverBackground}",
                    },
                },
            },
        },
    },

    components: {
        drawer: {
            root: {
                color: "#222",
            },
            title: {
                fontSize: "0.875rem",
            },
            // header / content / footer padding now come from
            // `overlay.modal.padding` (= spacing.md / 12px).
        },
        tabs: {
            tablist: {
                background: "transparent",
                borderWidth: "0",
                borderColor: "transparent",
            },
            tab: {
                background: "transparent",
                hoverBackground: "{surface.100}",
                activeBackground: "transparent",
                // Figma: idle text is #222222 (text.primary), active state is
                // #0477d1 with a primary-color bottom border underline. The
                // previous translucent-blue background style is replaced by
                // an explicit underline border per the design tokens.
                borderWidth: "0 0 1px 0",
                borderColor: "{surface.200}",
                hoverBorderColor: "{surface.200}",
                activeBorderColor: "{primary.color}",
                color: "{text.primary}",
                hoverColor: "{text.primary}",
                activeColor: "{primary.color}",
                padding: "{spacing.sm} {spacing.md}",
                fontWeight: "400",
                margin: "0",
                gap: "{spacing.xs}",
                fontSize: "{fontSize.sm}",
            },
            tabpanel: {
                background: "transparent",
                color: "{text.primary}",
                padding: "{spacing.md} 0",
            },
            activeBar: {
                height: "0",
                background: "transparent",
            },
        },
        datatable: {
            columnTitle: {
                fontWeight: "400",
            },
            header: {
                padding: "0",
                background: "transparent",
                borderWidth: "0",
            },
            headerCell: {
                padding: "var(--qmodii-spacing-sm)",
            },
            bodyCell: {
                padding: "var(--qmodii-spacing-sm)",
            },
            // Override PrimeVue's 0.875rem default so sort icons read at 12px.
            sortIcon: {
                size: "12px",
            },
        },

        tag: {
            root: {
                borderRadius: "0",
                fontSize: "12px",
            },

            colorScheme: {
                light: {
                    success: {
                        background: "#F2F9F2",
                        color: "#51A451",
                    },

                    warn: {
                        color: "#bc8827",
                        background: "#fffae7",
                    },

                    danger: {
                        color: "#C71C28",
                        background: "rgba(212, 87, 69, 0.1)",
                    },
                },
            },
        },

        message: {
            root: {
                borderRadius: "4px",
            },
            content: {
                padding: "8px",

                lg: {
                    padding: "16px",
                },
            },
        },
        // Figma `toast/border/radius: 6` — slightly rounder than the form
        // chrome (4px) since toasts float above content as overlays.
        toast: {
            root: {
                borderRadius: "6px",
            },
        },
        card: {
            root: {
                borderRadius: "{border.radius.md}",
                borderWidth: "{border.width.thin}",
                shadow: "{shadows.none}",
                transitionDuration: "{transition.duration}",
            },
            body: {
                padding: "{spacing.lg}",
            },
            title: {
                fontSize: "{fontSize.xl}",
                fontWeight: "600",
                lineHeight: "1.2",
                margin: "0 0 {spacing.sm} 0",
            },
            subtitle: {
                fontSize: "{fontSize.sm}",
                lineHeight: "1.4",
                margin: "0 0 {spacing.md} 0",
            },
        },
        floatlabel: {
            fontWeight: "400",
            color: "{surface.700}",
        },
        selectbutton: {
            // Figma SelectButton renders the GROUP as a single gray
            // (#f6f6f6) pill-shaped track with a small inset, and the
            // togglebuttons inside are transparent — only the checked
            // segment lights up as a white pill (the togglebutton content's
            // checked background). Aura's selectbutton preset exposes no
            // `background` token, so the track + sizing live in the `css`
            // hook below. Putting it here (rather than a separate global
            // stylesheet) means it ships as part of the theme and applies
            // wherever PrimeVue is configured — app, Storybook, AND the
            // Playwright CT/visual environment — so baselines match the app.
            root: {
                borderRadius: "{border.radius.sm}",
                padding: "2px",
            },
            css: () => `
.p-selectbutton {
    height: 32px;
    min-height: 32px;
    background: var(--qmodii-surface-50);
    padding: 2px;
    box-sizing: border-box;
}
.p-selectbutton .p-togglebutton {
    border: none;
    height: auto;
    min-height: 0;
    padding: 0;
}
`,
        },
        togglebutton: {
            // PrimeVue 4.5.x split togglebutton into root (wrapper) and
            // content (each individual button). The inner button's padding
            // lives under content, not at the top level — without this
            // override the labels ("Single Leg", "Multi Leg") wrapped onto
            // two lines when the toggle was used inside a tight flex row.
            root: {
                fontWeight: "400",
            },
            content: {
                padding: "{spacing.xs} {spacing.md}",
            },
            // ToggleButton sits transparently inside its SelectButton
            // parent (which now provides the gray track) — so each
            // segment shows the track behind it until checked, at which
            // point `content.checkedBackground` paints a white pill on
            // top. Border is collapsed to the same color to avoid a
            // visible outline when the gray track wraps each segment.
            colorScheme: {
                light: {
                    root: {
                        background: "transparent",
                        checkedBackground: "transparent",
                        hoverBackground: "transparent",
                        borderColor: "transparent",
                        checkedBorderColor: "transparent",
                    },
                },
            },
            // 4.5.x dropped the implicit `white-space: nowrap` on toggle
            // labels, so two-word labels ("Single Leg") wrapped at 32px.
            // Standalone togglebuttons are pinned to 32px; inside a
            // SelectButton the selectbutton css hook overrides back to auto.
            css: () => `
.p-togglebutton {
    height: 32px;
    min-height: 32px;
}
.p-togglebutton-label {
    white-space: nowrap;
}
`,
        },
        button: {
            // 4.5.x nests severity-specific tokens under colorScheme.{light,dark}.
            // root.{severity}. Setting at the top level (button.secondary.*)
            // generates the wrong CSS var path and silently no-ops.
            // Figma button/default/secondary tokens: color #222 (surface.800),
            // hover color #1e1e1e (surface.900) — darker than Aura's default
            // surface.600/700 so secondary buttons read with the same weight
            // as body text.
            colorScheme: {
                light: {
                    root: {
                        secondary: {
                            borderColor: "{border.color.primary}",
                            color: "{surface.800}",
                            hoverColor: "{surface.900}",
                            activeColor: "{surface.900}",
                        },
                    },
                },
                dark: {
                    root: {
                        secondary: {
                            borderColor: "{border.color.primary}",
                        },
                    },
                },
            },
            // Pin the default/sm-less/lg-less, non-icon-rounded button to the
            // app's 32px control height (Aura sizes via padding only).
            css: () => `
.p-button:not(.p-button-lg):not(.p-button-sm):not(.p-button-icon-only.p-button-rounded) {
    height: 32px;
    min-height: 32px;
}
`,
        },
        accordion: {
            panel: {
                borderWidth: "{border.width.none}",
            },
            header: {
                background: "{surface.100}",
                padding: "{spacing.sm} {spacing.md}",
                borderColor: "{surface.200}",
                borderWidth: "0",
                color: "{text.primary}",
                fontSize: "{fontSize.base}",
                fontWeight: "500",
                hoverBackground: "{surface.200}",
                focusBackground: "{surface.200}",
                activeBackground: "{surface.100}",
                activeColor: "{text.primary}",
                // 4.5.x moved the toggle-icon tokens under header — they used
                // to live at accordion.toggleIcon at the top level.
                toggleIcon: {
                    color: "{text.muted.color}",
                    hoverColor: "{text.primary}",
                    size: "{fontSize.base}",
                },
            },
            content: {
                background: "transparent",
                padding: "{spacing.none}",
                borderWidth: "{border.width.none}",
                color: "{text.primary}",
            },
        },
        panel: {
            root: {
                borderRadius: "{border.radius.sm}",
                shadow: "{shadows.none}",
            },
            content: {
                padding: "{spacing.md}",
            },
            // 4.5.x flattened the previous `panel.toggleable.header` nesting
            // into a single `toggleableHeader` key.
            toggleableHeader: {
                padding: "{spacing.md}",
            },
            header: {
                padding: "0",
            },
        },
        select: {
            maxHeight: "max(300px, 50vh) !important",
            // Figma `select/dropdown/width: 35` — Aura's 2.5rem (40px) is
            // a touch wide for our 32px-tall trigger.
            dropdown: {
                width: "35px",
            },
            // Pin the trigger to 32px and centre its label. The font-size
            // override is needed because PrimeVue's Select template hardcodes
            // `font-size: 1rem` on `.p-select-label` with no design token.
            css: () => `
.p-select {
    height: 32px;
    min-height: 32px;
}
.p-select .p-select-label {
    display: flex;
    align-items: center;
    min-height: 100%;
    font-size: inherit;
}
`,
        },
        multiselect: {
            // Min-height (not a fixed height) so chip-display multiselects can
            // grow to multiple rows (e.g. the MarketScreener filter sidebar);
            // centre the single-line label container.
            css: () => `
.p-multiselect {
    min-height: 32px;
}
.p-multiselect .p-multiselect-label-container {
    display: flex;
    align-items: center;
    min-height: 100%;
}
`,
        },
        progressbar: {
            root: {
                height: "6px",
                borderRadius: "3px",
            },
        },
    },
});
